import {TicketLineItem} from '@wix/ambassador-events-v1-ticket-reservation/types'
import {
  TicketWithFormattedSalePeriod,
  getPricingOptions,
  getTicketPrice,
  hasPricingOptions,
  hasSeatingPlan,
  isDonationTicketDefinition,
  isFeeAdded,
  isFreeTicketDefinition,
  saleScheduled,
  saleScheduledAndHasTickets,
  saleStarted,
} from '@wix/wix-events-commons-statics'
import {SelectedTickets, State} from '../types/state'
import {getEvent} from './event'
import {getSeatsReservationQuantities} from './seating/places'
import {getSelectedTicketDonation, getSelectedTicketQuantity} from './selected-tickets'

export const getTickets = (state: State) => {
  if (state.demoTickets?.length) {
    return state.demoTickets
  }

  return state.tickets
}

export const getTicketById = (tickets: TicketWithFormattedSalePeriod[], id: string) =>
  tickets.find(ticket => ticket.id === id)

export const getTicketOrderIndexById = (tickets: wix.events.ticketing.TicketDefinition[], id: string) =>
  tickets.findIndex(ticket => ticket.id === id)

export const areAllTicketsSoldOut = (tickets: wix.events.ticketing.TicketDefinition[]) =>
  !tickets.find((ticket: wix.events.ticketing.TicketDefinition) => !!ticket.limitPerCheckout)

export const isFixedPriceTicketSelected = (
  tickets: wix.events.ticketing.TicketDefinition[],
  selectedTickets: SelectedTickets,
) => {
  const fixedPriceSelectedTickets = tickets.filter(
    definition =>
      !isFreeTicketDefinition(definition) &&
      !isDonationTicketDefinition(definition) &&
      Boolean(selectedTickets[definition.id]?.quantity),
  )
  return fixedPriceSelectedTickets.length > 0
}

export const isWixFeeTypeIsAdded = (ticket: wix.events.ticketing.TicketDefinition) =>
  isFeeAdded(ticket.wixFeeConfig?.type)

export const hasScheduledTickets = (tickets: wix.events.ticketing.TicketDefinition[]) =>
  Boolean(tickets.find(ticket => saleScheduled(ticket)))

export const hasTicketsOnSale = (tickets: wix.events.ticketing.TicketDefinition[]) =>
  Boolean(tickets.find(ticket => saleStarted(ticket)))

// For Ecom checkout development
export const getReservationData = (state: State) => {
  const ticketDetails: TicketLineItem[] = []

  if (hasSeatingPlan(getEvent(state))) {
    const quantities = getSeatsReservationQuantities(state)

    quantities.forEach(seat => {
      seat.ticketDetails.forEach(ticketDetail => {
        ticketDetails.push({
          ticketDefinitionId: seat.ticketDefinitionId,
          quantity: ticketDetail.capacity ?? 1,
          ticketInfo: {
            seatId: ticketDetail.seatId,
            guestPrice: ticketDetail.priceOverride,
            pricingOptionId: ticketDetail.pricingOptionId,
          },
        })
      })
    })
  } else {
    const tickets = getTickets(state)

    tickets.forEach(ticket => {
      if (isDonationTicketDefinition(ticket)) {
        ticketDetails.push({
          ticketDefinitionId: ticket.id,
          quantity: getSelectedTicketQuantity(state, ticket.id),
          ticketInfo: {
            guestPrice: getSelectedTicketDonation(state, ticket.id),
          },
        })
      } else if (hasPricingOptions(ticket)) {
        getPricingOptions(ticket).forEach(pricingOption => {
          ticketDetails.push({
            ticketDefinitionId: ticket.id,
            quantity: getSelectedTicketQuantity(state, ticket.id, pricingOption.id),
            ticketInfo: {
              pricingOptionId: pricingOption.id,
            },
          })
        })
      } else {
        ticketDetails.push({
          ticketDefinitionId: ticket.id,
          quantity: getSelectedTicketQuantity(state, ticket.id),
        })
      }
    })
  }

  return ticketDetails.filter(ticket => ticket.quantity > 0)
}

export const getTicketsPriceRange = (state: State) => {
  const tickets = getTickets(state)
  const allTicketsSoldOut = areAllTicketsSoldOut(tickets)
  const filteredTickets = allTicketsSoldOut ? tickets : tickets.filter(ticket => !!ticket.limitPerCheckout)

  const sortedPrices = filteredTickets
    .reduce((prices, ticket) => {
      if (isDonationTicketDefinition(ticket)) {
        if (ticket.pricing?.minPrice) {
          return [...prices, ticket.pricing.minPrice]
        }
      } else if (hasPricingOptions(ticket)) {
        return [...prices, ...getPricingOptions(ticket).map(option => option.price)]
      } else {
        const price = getTicketPrice(ticket)

        if (price) {
          return [...prices, price]
        }
      }

      return prices
    }, [])
    .sort((a, b) => Number(a.value) - Number(b.value))

  if (!sortedPrices.length) {
    return null
  }

  return {
    min: sortedPrices[0],
    max: sortedPrices[sortedPrices.length - 1],
  }
}

export const getEarliestScheduledTicket = (tickets: TicketWithFormattedSalePeriod[]) => {
  const scheduledTickets = tickets.filter(ticket => saleScheduledAndHasTickets(ticket) && ticket.salePeriod)

  if (!scheduledTickets.length) {
    return null
  }

  return scheduledTickets.reduce((earliest, ticket) => {
    if (!earliest || new Date(ticket.salePeriod.startDate) < new Date(earliest.salePeriod.startDate)) {
      return ticket
    }
    return earliest
  }, null as TicketWithFormattedSalePeriod | null)
}
